import { Button } from "@babackapp/ui/components/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@babackapp/ui/components/collapsible";
import { FormField } from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { LoadingButton } from "@babackapp/ui/components/loading-button";
import { CheckCircle2, ChevronDown, ExternalLink, XCircle } from "lucide-react";
import { useCurrentShopMember } from "@babackapp/private-api-client/client/use-current-shop-member";
import {
  WORKFLOW_WEBHOOK_DEFAULT_VERSION,
  type WorkflowWebhookVersion,
} from "@babackapp/tracking-status-registry";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { type ReactNode, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkflowsEditorConfig } from "../../context/workflows-editor-config-context";
import { useWorkflowsEditorRouting } from "../../context/workflows-editor-routing-context";
import { useWorkflowForm } from "../../context/workflows-editor-workflows-context";
import { useNodeFormPath } from "../../hooks/use-node-form-path";
import { useTriggerType } from "../../hooks/use-trigger-type";
import { PanelContent } from "../../lib/panel/panel-content";
import { isValidWebhookUrl } from "./webhook-url";

export function PanelWebhook() {
  const { t } = useTranslation("trackoffice");
  const form = useWorkflowForm();
  const { shopId, openWebhookSettings } = useWorkflowsEditorRouting();
  const { webhooks } = useWorkflowsEditorConfig();
  const triggerType = useTriggerType();
  const urlPath = useNodeFormPath("config.url");
  const versionPath = useNodeFormPath("config.version");

  const url = (urlPath ? form.watch(urlPath) : "") as string | undefined;
  const version = ((versionPath ? form.watch(versionPath) : undefined) ??
    WORKFLOW_WEBHOOK_DEFAULT_VERSION) as WorkflowWebhookVersion;
  const isUrlValid = isValidWebhookUrl(url);

  if (!urlPath) {
    return null;
  }

  return (
    <PanelContent
      title={t("workflowsEditor.webhook.panelTitle", "Webhook")}
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {t(
            "workflowsEditor.webhook.description",
            "Sends the event to your URL as a signed JSON POST request. A failed delivery is retried for up to one hour.",
          )}
        </p>

        <div className="flex flex-col gap-1.5">
          <span className="font-medium text-xs text-muted-foreground">
            {t("workflowsEditor.webhook.urlLabel", "URL")}
          </span>
          <FormField
            control={form.control}
            name={urlPath}
            render={({ field }) => (
              <Input
                {...field}
                value={(field.value as string | undefined) ?? ""}
                placeholder="https://"
                className="text-sm"
              />
            )}
          />
          <span className="text-xs text-muted-foreground">
            {t("workflowsEditor.webhook.version", "Payload version: v{version}", {
              version,
            })}
          </span>
          {url && !isUrlValid && (
            <span className="text-xs text-destructive">
              {t(
                "workflowsEditor.webhook.urlInvalid",
                "Enter a full URL starting with https://",
              )}
            </span>
          )}
        </div>

        {webhooks && (
          <WebhookTest
            key={`${url}-${triggerType}`}
            shopId={shopId}
            url={url}
            triggerType={triggerType}
            version={version}
            isUrlValid={isUrlValid}
            useSendTest={webhooks.useSendTest}
          />
        )}

        {webhooks && (
          <WebhookSample
            shopId={shopId}
            triggerType={triggerType}
            version={version}
            useSample={webhooks.useSample}
          />
        )}

        {openWebhookSettings && (
          <Button
            variant="link"
            size="sm"
            className="h-auto gap-1 p-0 text-xs"
            onClick={openWebhookSettings}
          >
            {t(
              "workflowsEditor.webhook.openSettings",
              "Signing secret and delivery log",
            )}
            <ExternalLink className="h-3 w-3" />
          </Button>
        )}
      </div>
    </PanelContent>
  );
}

type WebhooksAdapter = NonNullable<
  ReturnType<typeof useWorkflowsEditorConfig>["webhooks"]
>;
type WebhookTestResult = NonNullable<
  ReturnType<WebhooksAdapter["useSendTest"]>["data"]
>;

function WebhookTest({
  shopId,
  url,
  triggerType,
  version,
  isUrlValid,
  useSendTest,
}: {
  shopId: string;
  url: string | undefined;
  triggerType: string;
  version: WorkflowWebhookVersion;
  isUrlValid: boolean;
  useSendTest: WebhooksAdapter["useSendTest"];
}) {
  const { t } = useTranslation("trackoffice");
  const sendTest = useSendTest(shopId);
  const [result, setResult] = useState<WebhookTestResult | null>(null);

  const onSend = () =>
    sendTest.mutate(
      { url: url!.trim(), triggerType, version },
      { onSuccess: (data) => setResult(data) },
    );

  const renderButton = (canTest: boolean) => {
    const button = (
      <LoadingButton
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        isLoading={sendTest.isPending}
        disabled={!isUrlValid || !canTest}
        onClick={onSend}
      >
        {t("workflowsEditor.webhook.sendTest", "Send a test")}
      </LoadingButton>
    );

    return canTest ? (
      button
    ) : (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          {/* A disabled button fires no hover event: the wrapper carries it. */}
          <TooltipTrigger asChild>
            <span className="w-full">{button}</span>
          </TooltipTrigger>
          <TooltipContent>
            {t("workflowsEditor.webhook.adminOnly", "Reserved for admins")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Only the button waits for the role, disabled meanwhile. */}
      <Suspense fallback={renderButton(false)}>
        <WithTestPermission shopId={shopId} render={renderButton} />
      </Suspense>

      {result?.isSuccess && (
        <span className="flex items-center gap-1 text-xs text-green-600">
          <CheckCircle2 className="h-3 w-3" />
          {t(
            "workflowsEditor.webhook.testSuccess",
            "Received: HTTP {statusCode} in {durationMs} ms",
            { statusCode: result.statusCode, durationMs: result.durationMs },
          )}
        </span>
      )}
      {result && !result.isSuccess && (
        <span className="flex items-start gap-1 text-xs text-destructive break-all">
          <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
          {result.error}
        </span>
      )}
      {sendTest.isError && (
        <span className="text-xs text-destructive">
          {getTestErrorMessage(sendTest.error, t)}
        </span>
      )}
    </div>
  );
}

type TranslateFn = ReturnType<typeof useTranslation>["t"];

function getTestErrorMessage(error: unknown, t: TranslateFn): string {
  switch ((error as { code?: string } | null)?.code) {
    case "WEBHOOK_TEST_QUOTA_EXCEEDED":
      return t(
        "workflowsEditor.webhook.testRateLimited",
        "Too many tests in the last hour. Try again later.",
      );
    case "WEBHOOK_INVALID_URL":
      return t(
        "workflowsEditor.webhook.testRejected",
        "This URL cannot receive webhooks. Use a public https address.",
      );
    default:
      return t(
        "workflowsEditor.webhook.testFailed",
        "The test could not be sent. Try again.",
      );
  }
}

// Same rule as the API and the settings page (ADMIN/OWNER).
function WithTestPermission({
  shopId,
  render,
}: {
  shopId: string;
  render: (canTest: boolean) => ReactNode;
}) {
  const { member } = useCurrentShopMember(shopId);
  return render(member?.roleId === "ADMIN" || member?.roleId === "OWNER");
}

function WebhookSample({
  shopId,
  triggerType,
  version,
  useSample,
}: {
  shopId: string;
  triggerType: string;
  version: WorkflowWebhookVersion;
  useSample: WebhooksAdapter["useSample"];
}) {
  const { t } = useTranslation("trackoffice");
  const [isOpen, setIsOpen] = useState(false);
  const { data } = useSample(
    shopId,
    isOpen ? triggerType : undefined,
    version,
  );

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground">
        {t("workflowsEditor.webhook.samplePayload", "Example payload")}
        <ChevronDown
          className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-2 max-h-80 overflow-auto rounded-md bg-muted p-2 text-[10px] leading-snug">
          {data ? JSON.stringify(data, null, 2) : "…"}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}
